import { useState } from 'react'

import { ApolloError } from '@apollo/client'
import { GetMarketplaceInfoDocument } from 'generated/resources/graphql'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import app from 'modules/core/store/models/app'
import { getMarketplaceGraphqlClient } from 'modules/it_management/lib/marketplaceGraphqlClient'
import { PLACE_ORDERS } from 'modules/it_management/lib/marketplaceOrderGraphql'

import { useDeviceCatalogCart } from './useDeviceCatalogCart'

export type UseDeviceCatalogCheckoutOptions = {
  onSuccess?: () => void
}

export type DeviceCatalogCheckoutPayload = {
  customerName: string
  phoneNumber: string
  consentUserData: boolean
  deliveryAddress?: string
}

const DEFAULT_CURRENCY = 'EUR'
const DEFAULT_COMPANY_VAT = 'N/A'

const toNonEmpty = (...values: (string | null | undefined)[]) =>
  values.find((v) => v?.trim())?.trim()

export function useDeviceCatalogCheckout(
  cart: ReturnType<typeof useDeviceCatalogCart>,
  options?: UseDeviceCatalogCheckoutOptions
) {
  const { company, access, email } = useCurrentSession()
  const [isCheckingOut, setIsCheckingOut] = useState(false)
  const [fetchMarketplaceInfo] = useLazyQuery(GetMarketplaceInfoDocument)
  const marketplaceClient = getMarketplaceGraphqlClient()

  const handleCheckout = async ({
    customerName,
    phoneNumber,
    consentUserData,
    deliveryAddress: selectedDeliveryAddress,
  }: DeviceCatalogCheckoutPayload) => {
    const cartId = cart.cartId
    if (cart.items.length === 0 || !cartId) return

    setIsCheckingOut(true)

    track(trackingEvents.itManagement.deviceCatalogEvents.cartCheckoutStart, {
      item_count: cart.itemCount,
      total: cart.total,
      company_id: company.id,
      user_id: access.id,
    })

    app.setProgress({
      message: i18n.t('it_management.device_catalog.cart.checkout_submitting'),
    })

    try {
      const marketplaceInfo = await fetchMarketplaceInfo()
      const mainLegalEntity =
        marketplaceInfo.data?.apiCore?.currentsConnection?.edges?.[0]?.node?.company?.legalEntities?.find(
          (le) => le?.main
        )
      const mainLocation = marketplaceInfo.data?.locations?.locationsConnection?.edges?.[0]?.node

      const companyVat =
        toNonEmpty(mainLegalEntity?.tin) ??
        toNonEmpty(
          company.slug,
          typeof company.id === 'string' ? `company-${company.id}` : undefined
        ) ??
        DEFAULT_COMPANY_VAT

      const fallbackCustomerName =
        toNonEmpty(
          access.fullName,
          [access.firstName, access.lastName].filter(Boolean).join(' '),
          email
        ) ?? String(access.id)

      const resolvedCustomerName =
        toNonEmpty(customerName, fallbackCustomerName) ?? fallbackCustomerName

      const companyName = toNonEmpty(company.name) ?? 'N/A'
      const deliveryAddress =
        toNonEmpty(selectedDeliveryAddress) ??
        toNonEmpty(
          [
            mainLocation?.addressLine1,
            mainLocation?.addressLine2,
            mainLocation?.city,
            mainLocation?.state,
            mainLocation?.postalCode,
            mainLocation?.country,
          ]
            .filter(Boolean)
            .join(', ')
        )

      const result = await marketplaceClient.mutate({
        mutation: PLACE_ORDERS,
        variables: {
          cartId,
          companyName,
          companyVat,
          currency: DEFAULT_CURRENCY,
          customerEmail: email ?? '',
          customerName: resolvedCustomerName,
          deliveryAddress,
          consentUserData,
          phoneNumber,
        },
      })

      app.setProgress(null)

      const errors = result.errors

      if (errors?.length) {
        const rawMessage = errors.map((e) => e.message).join(' ')
        track(trackingEvents.itManagement.deviceCatalogEvents.cartCheckoutError, {
          error_type: 'backend_error',
          error_message: rawMessage,
          item_count: cart.itemCount,
          total: cart.total,
          cart_id: cartId,
          company_id: company.id,
          user_id: access.id,
        })
        app.setFlash({
          type: 'wrong',
          message: rawMessage,
        })
        return
      }

      track(trackingEvents.itManagement.deviceCatalogEvents.cartCheckoutSuccess, {
        item_count: cart.itemCount,
        total: cart.total,
        cart_id: cartId,
        company_id: company.id,
        user_id: access.id,
      })
      app.setFlash({
        type: 'correct',
        message: i18n.t('it_management.device_catalog.cart.checkout_success'),
      })
      cart.clearCart()
      cart.closeCart()
      options?.onSuccess?.()
    } catch (error) {
      app.setProgress(null)

      const errorMessage =
        error instanceof ApolloError && error.graphQLErrors?.length
          ? error.graphQLErrors.map((e) => e.message).join(' ')
          : error instanceof Error
            ? error.message
            : i18n.t('it_management.device_catalog.cart.checkout_error')

      track(trackingEvents.itManagement.deviceCatalogEvents.cartCheckoutError, {
        error_type: error instanceof ApolloError ? 'backend_error' : 'network_error',
        error_message: errorMessage,
        item_count: cart.itemCount,
        total: cart.total,
        cart_id: cartId,
        company_id: company.id,
        user_id: access.id,
      })

      app.setFlash({
        type: 'wrong',
        message: errorMessage,
      })
    } finally {
      setIsCheckingOut(false)
    }
  }

  return { handleCheckout, isCheckingOut }
}
